import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, FileText, Layers, RefreshCw, Copy, Download, ChevronRight, Sparkles } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { ScrollArea } from '../ui/scroll-area';

/**
 * ChangeOrdersTab — list of saved change orders + Markdown viewer.
 *
 * A Change Order is the consolidated brief that aggregates multiple findings
 * into a single Markdown document with verbatim user quotes, embedded mini-PRDs,
 * and a consolidated change list ready to hand to Emergent / Cursor / Claude.
 */
export default function ChangeOrdersTab() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await tl.listChangeOrders();
      setItems(data.items || []);
      // Honour ?co=...
      const params = new URLSearchParams(location.search);
      const coId = params.get('co');
      if (coId) {
        const found = (data.items || []).find((i) => i.change_order_id === coId);
        if (found) setActive(found);
      } else if ((data.items || []).length && !active) {
        setActive(data.items[0]);
      }
    } catch (e) {
      toast.error('Failed to load change orders: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line

  const copyMd = async () => {
    if (!active?.markdown) return;
    try {
      await navigator.clipboard.writeText(active.markdown);
      toast.success('Markdown copied — paste into your code agent');
    } catch { toast.error('Copy failed'); }
  };

  const downloadMd = () => {
    if (!active?.markdown) return;
    const blob = new Blob([active.markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.change_order_id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="grid grid-cols-12 gap-3 h-[80vh]" data-testid="change-orders-tab">
      {/* List */}
      <div className="col-span-4 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="outline" className="text-xs">{items.length} change orders</Badge>
          <Button size="sm" variant="ghost" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="space-y-2 pr-2">
            {items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm space-y-2" data-testid="co-empty">
                <Sparkles className="w-8 h-8 mx-auto opacity-50" />
                <div>No change orders yet.</div>
                <p className="text-xs">Select findings on the Findings tab and click "Generate Change Order".</p>
              </div>
            ) : items.map((co) => {
              const isActive = co.change_order_id === active?.change_order_id;
              return (
                <button
                  key={co.change_order_id}
                  onClick={() => setActive(co)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    isActive ? 'border-violet-500 bg-violet-500/10' : 'border-border hover:bg-accent'
                  }`}
                  data-testid={`co-row-${co.change_order_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-xs font-mono">{co.change_order_id}</span>
                    <ChevronRight className="w-3 h-3 ml-auto opacity-50" />
                  </div>
                  <div className="text-sm font-medium leading-tight mb-1.5">{co.title}</div>
                  <div className="flex flex-wrap gap-1.5 text-[10px]">
                    <Badge variant="outline">{co.finding_count} findings</Badge>
                    {co.fail_count > 0 && <Badge variant="destructive">{co.fail_count} fail</Badge>}
                    {co.note_count > 0 && <Badge variant="outline" className="text-amber-700 border-amber-500/40">{co.note_count} note</Badge>}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1.5">
                    {(co.routes_affected || []).slice(0, 3).join(', ')}
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Viewer */}
      <div className="col-span-8 flex flex-col">
        {active ? (
          <Card className="flex-1 flex flex-col overflow-hidden" data-testid="co-viewer">
            <div className="px-4 py-2 border-b flex items-center justify-between gap-2 bg-muted/40">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-violet-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{active.title}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{active.change_order_id}</div>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={downloadMd} data-testid="co-download">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                </Button>
                <Button size="sm" onClick={copyMd} data-testid="co-copy" className="bg-violet-600 hover:bg-violet-700 text-white">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy markdown
                </Button>
              </div>
            </div>
            <ScrollArea className="flex-1">
              <pre className="text-xs whitespace-pre-wrap p-4 font-mono leading-relaxed">{active.markdown}</pre>
            </ScrollArea>
          </Card>
        ) : (
          <Card className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
            Select a change order to view
          </Card>
        )}
      </div>
    </div>
  );
}
